import { Box, IconButton, Tooltip } from '@mui/material';
import { ActionButton } from 'components/atoms';
import { InfoSimpleModal } from 'components/organisms/modals';
import { noop } from 'lodash';
import React, { useState } from 'react';
import { FaEdit, FaEye, FaUserPlus, FaCheckSquare, FaCheck } from 'react-icons/fa';
import {
  FaChartBar,
  FaFileMedical,
  FaUserMinus,
  FaPlus,
  FaDownload,
} from 'react-icons/fa6';
import { IoMdTrash } from 'react-icons/io';

import { ActionFlexBoxProps } from './types';

export const ActionsFlexBox = ({
  msg = '',
  activateOnClick = noop,
  addOnClick,
  addPatientOnClick = noop,
  archiveOnClick = noop,
  completeOnClick = noop,
  downloadOnClick = noop,
  deleteOnClick,
  editOnClick,
  reportOnClick = noop,
  viewOnClick,
  openInNewOnClick,
  actionButtons,
  disableAll = false,
  disableDeleteTooltip,
  disableEditTooltip,
  disableEdit = false,
  disableDelete = false,
  disableComplete = false,
}: ActionFlexBoxProps) => {
  const [openArchive, setOpenArchive] = useState(false);
  const [openActive, setOpenActive] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openComplete, setOpenComplete] = useState(false);
  const handleOpenArchive = () => setOpenArchive(true);
  const handleCloseArchive = () => setOpenArchive(false);
  const handleOpenDelete = () => setOpenDelete(true);
  const handleCloseDelete = () => setOpenDelete(false);
  const handleOpenActive = () => setOpenActive(true);
  const handleCloseActive = () => setOpenActive(false);
  const handleOpenComplete = () => setOpenComplete(true);
  const handleCloseComplete = () => setOpenComplete(false);
  const {
    activateButton = false,
    addButton = false,
    addPatient = false,
    archiveButton = false,
    downloadButton = false,
    deleteButton = false,
    editButton = false,
    reportButton = false,
    viewButton = false,
    openNewTabButton = false,
    completeButton = false,
  } = actionButtons;

  const handleDelete = () => {
    if (deleteOnClick) deleteOnClick();
    setOpenDelete(false);
  };

  return disableAll ? null : (
    <Box
      sx={{ display: 'flex', gap: 3, alignItems: 'center', justifyContent: 'flex-start' }}
    >
      {viewButton && <ActionButton Icon={FaEye} onClick={viewOnClick} tooltip={'View'} />}
      {editButton && !disableEdit ? (
        <ActionButton
          disabled={Boolean(disableEditTooltip)}
          Icon={FaEdit}
          onClick={disableEditTooltip ? noop : editOnClick}
          tooltip={disableEditTooltip || 'Edit'}
        />
      ) : null}

      {reportButton && (
        <ActionButton Icon={FaChartBar} onClick={reportOnClick} tooltip={'Report'} />
      )}
      {archiveButton && !disableEdit && (
        <ActionButton
          Icon={FaUserMinus}
          onClick={handleOpenArchive}
          tooltip={'Archive'}
        />
      )}
      {activateButton && !disableEdit && (
        <ActionButton
          Icon={FaUserPlus}
          onClick={handleOpenActive}
          tooltip={'Set Patient to Active'}
        />
      )}
      {completeButton && !disableComplete && (
        <ActionButton
          Icon={FaCheckSquare}
          onClick={handleOpenComplete}
          tooltip={'Done'}
        />
      )}
      {deleteButton && !disableDelete ? (
        <ActionButton
          disabled={Boolean(disableDeleteTooltip)}
          Icon={IoMdTrash}
          onClick={disableDeleteTooltip ? noop : handleOpenDelete}
          tooltip={disableDeleteTooltip || 'Delete'}
        />
      ) : null}
      {addButton && <ActionButton Icon={FaPlus} onClick={addOnClick} tooltip={'Add'} />}
      {addPatient && (
        <ActionButton
          Icon={FaFileMedical}
          onClick={addPatientOnClick}
          tooltip={'Add To My Patient'}
        />
      )}
      {downloadButton && (
        <Tooltip title="Download" placement="top">
          <IconButton
            disabled={disableAll}
            size="small"
            sx={{ padding: 0 }}
            onClick={downloadOnClick}
          >
            <FaDownload />
          </IconButton>
        </Tooltip>
      )}
      {openNewTabButton && (
        <ActionButton
          Icon={FaChartBar}
          onClick={openInNewOnClick}
          tooltip={'Create Report View'}
        />
      )}
      <InfoSimpleModal
        icon={FaUserMinus}
        open={openArchive}
        onClose={handleCloseArchive}
        onSubmit={() => {
          archiveOnClick();
          handleCloseArchive();
        }}
        successButton={false}
        text={[`You are about to archive this${msg ? ` ${msg}.` : '.'}`, 'Are you sure?']}
        title="WARNING"
      />
      <InfoSimpleModal
        icon={IoMdTrash}
        open={openDelete}
        onClose={handleCloseDelete}
        onSubmit={handleDelete}
        successButton={false}
        text={[`You are about to delete this${msg ? ` ${msg}.` : '.'}`, 'Are you sure?']}
        title="WARNING"
      />
      <InfoSimpleModal
        icon={FaUserPlus}
        open={openActive}
        onClose={handleCloseActive}
        onSubmit={() => {
          activateOnClick();
          handleCloseActive();
        }}
        successButton={true}
        text={[
          `You are about to make this patient active${msg ? ` ${msg}.` : '.'}`,
          'Are you sure?',
        ]}
        title="WARNING"
      />
      <InfoSimpleModal
        icon={FaCheck}
        open={openComplete}
        onClose={handleCloseComplete}
        onSubmit={() => {
          completeOnClick();
          handleCloseComplete();
        }}
        successButton={true}
        text={[
          `You are about to mark this ${msg ? ` ${msg}` : '.'} as DONE. This will remove it from this table. Are you sure?`,
        ]}
        title="WARNING"
      />
    </Box>
  );
};
